import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Eye, EyeSlash, Warning } from 'phosphor-react';
import { toast } from 'sonner';

const Start = () => {
  const [passcode, setPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { createPasscode } = useAuth();
  const navigate = useNavigate();

  const handleCreatePasscode = async (e) => {
    e.preventDefault();

    if (!agreedToTerms) {
      toast.error('Please acknowledge the terms');
      return;
    }

    if (passcode.length < 6) {
      toast.error('Passcode must be at least 6 characters');
      return;
    }

    if (passcode !== confirmPasscode) {
      toast.error('Passcodes do not match');
      return;
    }

    setIsCreating(true);
    try {
      await createPasscode(passcode);
      toast.success('Welcome to AraVela');
      navigate('/chat');
    } catch (error) {
      toast.error('Failed to create passcode');
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 noise-texture flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Lock size={32} weight="duotone" className="text-primary" />
          </div>
          <h1 className="text-4xl font-serif font-semibold text-stone-800 mb-2" data-testid="start-title">
            Create Your Lock
          </h1>
          <p className="text-stone-600">
            Choose a passcode to protect your sanctuary
          </p>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-sm mb-6">
          <form onSubmit={handleCreatePasscode} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Passcode (min 6 characters)
              </label>
              <div className="relative">
                <input
                  type={showPasscode ? 'text' : 'password'}
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  className="w-full bg-stone-50 border-transparent focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 transition-all"
                  placeholder="Enter passcode"
                  data-testid="passcode-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasscode(!showPasscode)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  data-testid="toggle-passcode-visibility"
                >
                  {showPasscode ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Confirm Passcode
              </label>
              <input
                type={showPasscode ? 'text' : 'password'}
                value={confirmPasscode}
                onChange={(e) => setConfirmPasscode(e.target.value)}
                className="w-full bg-stone-50 border-transparent focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 transition-all"
                placeholder="Confirm passcode"
                data-testid="confirm-passcode-input"
                required
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <Warning size={24} weight="duotone" className="text-amber-600 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <strong className="block mb-1">No Recovery Available</strong>
                  If you forget this passcode, your data cannot be recovered. This is real encryption.
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 rounded border-stone-300 text-primary focus:ring-primary"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className="text-sm text-stone-600">
                I understand that AraVela is not therapy, stores all data locally with encryption, and cannot recover my passcode.
              </label>
            </div>

            <button
              type="submit"
              disabled={isCreating}
              className="w-full bg-primary text-white rounded-full px-8 py-4 font-medium hover:shadow-soft transition-all duration-300 active:scale-95 disabled:opacity-50"
              data-testid="create-lock-btn"
            >
              {isCreating ? 'Creating...' : 'Create Lock'}
            </button>
          </form>
        </div>

        <div className="text-center text-sm text-stone-500">
          Already have a lock?{' '}
          <button
            onClick={() => navigate('/unlock')}
            className="text-primary hover:underline"
            data-testid="go-to-unlock-link"
          >
            Unlock here
          </button>
        </div>
      </div>
    </div>
  );
};

export default Start;